import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  api,
  type DocumentReadingSummary,
  type DocumentSummary,
  type Group,
  type PendingReader,
  type ReadingEntry,
} from '../api/client';
import { useSession } from '../session/SessionContext';
import { AvisoError } from '../components/Aviso';
import { Modal } from '../components/Modal';
import { Paginacion } from '../components/Paginacion';
import { BuscadorDePersona } from '../components/BuscadorDePersona';
import { duracionLegible } from '../formato';
import { claveDeConsulta, claveVisible } from '../documentos/clave';
import { AccionIcono } from '../components/AccionIcono';
import { IconoVer } from '../components/Iconos';

type Vista = 'consultas' | 'documentos';

/**
 * Bitácora de accesos: quién consultó qué, cuántas veces y cuánto tiempo.
 *
 * Es lo que acredita difusión normativa ante una revisión. La pregunta que la
 * cierra no es quién leyó sino **quién falta**, así que esa consulta está aquí
 * al mismo nivel que las otras, no escondida.
 */
export function Consultas({ grupos }: { grupos: Group[] }) {
  const { t, i18n } = useTranslation();
  const { puede } = useSession();
  const [parametros, setParametros] = useSearchParams();

  const [vista, setVista] = useState<Vista>('consultas');
  const [entradas, setEntradas] = useState<ReadingEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [porPagina, setPorPagina] = useState(25);
  const [porDocumento, setPorDocumento] = useState<DocumentReadingSummary[]>([]);
  const [pendientes, setPendientes] = useState<PendingReader[] | null>(null);
  const [documentos, setDocumentos] = useState<DocumentSummary[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [cargando, setCargando] = useState(true);

  /** Qué se está mirando de cerca: una consulta suelta o el uso de un documento. */
  const [consultaAbierta, setConsultaAbierta] = useState<ReadingEntry | null>(null);
  const [documentoAbierto, setDocumentoAbierto] = useState<DocumentReadingSummary | null>(null);
  const [informe, setInforme] = useState<{
    readers: ReadingEntry[];
    neverOpened: PendingReader[];
  } | null>(null);

  const fechaHora = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const consulta = parametros.toString();
  const documentoElegido = parametros.get('documentId') ?? '';

  const fijar = useCallback(
    (clave: string, valor: string) => {
      const siguientes = new URLSearchParams(parametros);
      if (valor) siguientes.set(clave, valor);
      else siguientes.delete(clave);
      siguientes.delete('page');
      setParametros(siguientes, { replace: true });
    },
    [parametros, setParametros],
  );

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    Promise.all([api.accessLog(consulta), api.accessLogByDocument(consulta)])
      .then(([pagina, resumen]) => {
        if (!vigente) return;
        setEntradas(pagina.items);
        setTotal(pagina.totalItems);
        setPorPagina(pagina.pageSize);
        setPorDocumento(resumen);
        setError(null);
      })
      .catch((e) => vigente && setError(e))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [consulta]);

  useEffect(() => {
    api.documents('?pageSize=100').then((pagina) => setDocumentos(pagina.items)).catch(setError);
  }, [puede]);

  // Quién falta por leerlo sólo tiene sentido con un documento elegido: sin él,
  // la pregunta no existe.
  useEffect(() => {
    if (!documentoAbierto) {
      setInforme(null);
      return;
    }
    setInforme(null);
    api
      .documentAccessReport(documentoAbierto.documentId, consulta)
      .then((datos) => setInforme({ readers: datos.readers, neverOpened: datos.neverOpened }))
      .catch(setError);
  }, [documentoAbierto, consulta]);

  useEffect(() => {
    if (!documentoElegido) {
      setPendientes(null);
      return;
    }
    api
      .documentAccessReport(documentoElegido, consulta)
      .then((informe) => setPendientes(informe.neverOpened))
      .catch(setError);
  }, [documentoElegido, consulta]);

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('access.title')}</h1>
        <p className="pagina__bajada">{t('access.subtitle')}</p>
      </div>

      <AvisoError error={error} />

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('access.filters')}</h2>
          <span className="tarjeta__nota">
            {cargando ? t('common.loading') : t('access.count', { count: total })}
          </span>
          {puede('audit.export') && (
            /* Un enlace normal, no fetch: el navegador tiene que recibir el
               archivo con su nombre, y la cookie viaja sola. */
            <a className="boton boton--discreto" href={`/api/v1/access-log/export?${consulta}`}>
              {t('access.export')}
            </a>
          )}
        </div>

        <div className="filtros">
          <div className="campo">
            <label htmlFor="documentId">{t('access.document')}</label>
            <select
              id="documentId"
              value={documentoElegido}
              onChange={(e) => fijar('documentId', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              {documentos.map((documento) => (
                <option key={documento.id} value={documento.id}>
                  {claveVisible(documento)} · {documento.title}
                </option>
              ))}
            </select>
          </div>

          {puede('user.manage') && (
            <BuscadorDePersona
              etiqueta={t('access.person')}
              valor={parametros.get('userId') ?? ''}
              onElegir={(userId) => fijar('userId', userId)}
            />
          )}

          <div className="campo">
            <label htmlFor="groupId">{t('documents.group')}</label>
            <select
              id="groupId"
              value={parametros.get('groupId') ?? ''}
              onChange={(e) => fijar('groupId', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              {grupos.map((grupo) => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.name}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="from">{t('access.from')}</label>
            <input
              id="from"
              type="date"
              value={parametros.get('from')?.slice(0, 10) ?? ''}
              onChange={(e) => fijar('from', e.target.value)}
            />
          </div>

          <div className="campo">
            <label htmlFor="to">{t('access.to')}</label>
            <input
              id="to"
              type="date"
              value={parametros.get('to')?.slice(0, 10) ?? ''}
              onChange={(e) => fijar('to', e.target.value)}
            />
          </div>
        </div>

        <div className="pestanas" role="tablist">
          {(['consultas', 'documentos'] as const).map((cual) => (
            <button
              key={cual}
              type="button"
              role="tab"
              aria-selected={vista === cual}
              className={`pestana${vista === cual ? ' pestana--activa' : ''}`}
              onClick={() => setVista(cual)}
            >
              {t(`access.tab.${cual}`)}
            </button>
          ))}
        </div>

        {vista === 'consultas' ? (
          entradas.length === 0 ? (
            <div className="vacio">
              <span className="vacio__titulo">{t('access.empty')}</span>
              <span>{t('access.emptyHint')}</span>
            </div>
          ) : (
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>{t('access.person')}</th>
                    <th>{t('access.document')}</th>
                    <th>{t('documents.version')}</th>
                    <th>{t('access.startedAt')}</th>
                    <th>{t('access.duration')}</th>
                    <th>{t('access.pages')}</th>
                    <th>{t('access.address')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entradas.map((entrada) => (
                    <tr key={entrada.sessionId}>
                      <td>
                        {entrada.fullName}
                        <br />
                        <span className="tenue mono" style={{ fontSize: '.75rem' }}>
                          {entrada.username}
                        </span>
                      </td>
                      <td>
                        <span className="mono">{claveDeConsulta(entrada)}</span>
                        <br />
                        <span className="tenue" style={{ fontSize: '.78rem' }}>
                          {entrada.documentTitle}
                        </span>
                      </td>
                      <td className="mono">
                        {t('documents.versionShort', { number: entrada.versionLabel })}
                      </td>
                      <td className="mono">{fechaHora.format(new Date(entrada.startedAt))}</td>
                      <td className="mono">{duracionLegible(entrada.activeSeconds)}</td>
                      <td className="mono">{entrada.pagesViewed}</td>
                      <td className="mono">{entrada.ipAddress}</td>
                      <td>
                        <AccionIcono
                          etiqueta={t('access.detail')}
                          icono={<IconoVer />}
                          onClick={() => setConsultaAbierta(entrada)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Paginacion total={total} porPagina={porPagina} cargando={cargando} />
            </div>
          )
        ) : porDocumento.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">{t('access.empty')}</span>
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('documents.code')}</th>
                  <th>{t('documents.name')}</th>
                  <th>{t('access.readers')}</th>
                  <th>{t('access.count', { count: 0 }).replace(/\d+\s*/, '')}</th>
                  <th>{t('access.totalTime')}</th>
                  <th>{t('access.lastRead')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {porDocumento.map((resumen) => (
                  <tr key={resumen.documentId}>
                    <td className="mono">{claveVisible(resumen)}</td>
                    <td>{resumen.title}</td>
                    <td className="mono">{resumen.readers}</td>
                    <td className="mono">{resumen.sessions}</td>
                    <td className="mono">{duracionLegible(resumen.totalSeconds)}</td>
                    <td className="mono">
                      {resumen.lastReadAt ? fechaHora.format(new Date(resumen.lastReadAt)) : '—'}
                    </td>
                    <td>
                      <AccionIcono
                        etiqueta={t('access.detail')}
                        icono={<IconoVer />}
                        onClick={() => setDocumentoAbierto(resumen)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>


      {/* Una consulta suelta: quién, qué versión abrió, cuánto estuvo y cómo
          terminó. El «cómo terminó» distingue quien cerró de quien se fue. */}
      {consultaAbierta && (
        <Modal titulo={t('access.readingDetail')} onCerrar={() => setConsultaAbierta(null)}>
          <div className="datos">
            <div className="dato">
              <span className="dato__etiqueta">{t('access.person')}</span>
              <span className="dato__valor">
                {consultaAbierta.fullName}
                <br />
                <span className="tenue mono" style={{ fontSize: '.75rem' }}>
                  {consultaAbierta.username}
                </span>
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.document')}</span>
              <span className="dato__valor">
                <span className="mono">{claveDeConsulta(consultaAbierta)}</span>
                <br />
                <span className="tenue" style={{ fontSize: '.78rem' }}>
                  {consultaAbierta.documentTitle}
                </span>
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('documents.version')}</span>
              <span className="dato__valor dato__valor--mono">
                {t('documents.versionShort', { number: consultaAbierta.versionLabel })}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.startedAt')}</span>
              <span className="dato__valor dato__valor--mono">
                {fechaHora.format(new Date(consultaAbierta.startedAt))}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.endedAt')}</span>
              <span className="dato__valor dato__valor--mono">
                {consultaAbierta.endedAt
                  ? fechaHora.format(new Date(consultaAbierta.endedAt))
                  : t('access.stillOpen')}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.duration')}</span>
              <span className="dato__valor dato__valor--mono">
                {duracionLegible(consultaAbierta.activeSeconds)}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.pages')}</span>
              <span className="dato__valor dato__valor--mono">{consultaAbierta.pagesViewed}</span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.address')}</span>
              <span className="dato__valor dato__valor--mono">{consultaAbierta.ipAddress}</span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.howItEnded')}</span>
              <span className="dato__valor">
                {consultaAbierta.closeReason
                  ? t(`access.closeReason.${consultaAbierta.closeReason}`, {
                      defaultValue: consultaAbierta.closeReason,
                    })
                  : t('access.stillOpen')}
              </span>
            </div>
          </div>
          <p className="tarjeta__pie">{t('dashboard.readingNote')}</p>
        </Modal>
      )}

      {/* Un documento: cuánto se ha usado, quiénes lo abrieron y **quiénes no**.
          Las dos mitades juntas son lo que acredita la difusión. */}
      {documentoAbierto && (
        <Modal
          titulo={`${claveVisible(documentoAbierto)} · ${documentoAbierto.title}`}
          onCerrar={() => setDocumentoAbierto(null)}
        >
          <div className="datos">
            <div className="dato">
              <span className="dato__etiqueta">{t('access.readers')}</span>
              <span className="dato__valor dato__valor--mono">{documentoAbierto.readers}</span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.timesOpened')}</span>
              <span className="dato__valor dato__valor--mono">{documentoAbierto.sessions}</span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.totalTime')}</span>
              <span className="dato__valor dato__valor--mono">
                {duracionLegible(documentoAbierto.totalSeconds)}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('access.lastRead')}</span>
              <span className="dato__valor dato__valor--mono">
                {documentoAbierto.lastReadAt
                  ? fechaHora.format(new Date(documentoAbierto.lastReadAt))
                  : '—'}
              </span>
            </div>
          </div>

          {!informe ? (
            <p className="tenue modal__seccion">{t('access.loading')}</p>
          ) : (
            <>
              <h3 className="modal__seccion">{t('access.whoRead')}</h3>
              {informe.readers.length === 0 ? (
                <p className="vacio">{t('access.empty')}</p>
              ) : (
                <div className="tabla-envoltura">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('access.person')}</th>
                        <th>{t('access.startedAt')}</th>
                        <th>{t('access.duration')}</th>
                        <th>{t('access.pages')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {informe.readers.map((lectura) => (
                        <tr key={lectura.sessionId}>
                          <td>{lectura.fullName}</td>
                          <td className="mono">
                            {fechaHora.format(new Date(lectura.startedAt))}
                          </td>
                          <td className="mono">{duracionLegible(lectura.activeSeconds)}</td>
                          <td className="mono">{lectura.pagesViewed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 className="modal__seccion">{t('access.pendingTitle')}</h3>
              {informe.neverOpened.length === 0 ? (
                <p className="vacio">{t('access.everyoneRead')}</p>
              ) : (
                <ul className="lista-etiquetas">
                  {informe.neverOpened.map((persona) => (
                    <li key={persona.userId} className="etiqueta">
                      {persona.fullName}
                    </li>
                  ))}
                </ul>
              )}
              <p className="tarjeta__pie">{t('access.pendingNote')}</p>
            </>
          )}
        </Modal>
      )}

      {pendientes && (
        <section className="tarjeta">
          <div className="tarjeta__encabezado">
            <h2 className="tarjeta__titulo">{t('access.pendingTitle')}</h2>
            <span className="tarjeta__nota">{pendientes.length}</span>
          </div>
          {pendientes.length === 0 ? (
            <p className="vacio">{t('access.everyoneRead')}</p>
          ) : (
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>{t('access.person')}</th>
                    <th>{t('users.username')}</th>
                    <th>{t('documents.owner')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientes.map((persona) => (
                    <tr key={persona.userId}>
                      <td>{persona.fullName}</td>
                      <td className="mono">{persona.username}</td>
                      <td>{persona.departmentName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="tarjeta__pie">{t('access.pendingNote')}</p>
        </section>
      )}
    </>
  );
}
